import React, {useMemo} from "react";
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import ChartCard from "./ChartCard.jsx";
import useStatisticsData from "./useStatisticsData.js";
import {formatNumber, GROUP_PALETTE, TREND_NEUTRAL_COLOR} from "./format.js";
import {formatPallets} from "./inventoryStrategyShared.jsx";

const CURRENT_COLOR = TREND_NEUTRAL_COLOR;
const TARGET_COLOR = GROUP_PALETTE[0];

/**
 * Flächenbilanz je Warengruppe (#5 im Auftrag): belegte gegen empfohlene
 * Paletten, als zwei nebeneinanderstehende Balken je Warengruppe - bewusst
 * kein Diagramm mit zwei Y-Achsen (siehe Vorgabe), beide Werte teilen sich
 * dieselbe Palettenskala. Warengruppen ohne bekannte Palettengröße (0 bei
 * beiden Werten) werden ausgeblendet, weil sie hier keine Aussage tragen.
 */
export default function InventoryGroupPalletsChart({params}) {
    const {data, loading, error} = useStatisticsData("/statistics/inventory-strategy/summary", params);

    const rows = useMemo(() => {
        const byGroup = data?.by_group || [];
        return byGroup
            .filter((row) => row.articles_with_known_pallet_size > 0)
            .map((row) => ({...row, label: row.article_group || "Ohne Warengruppe"}))
            .sort((a, b) => b.pallets_target - a.pallets_target);
    }, [data]);

    const height = Math.max(240, rows.length * 46);

    return (
        <ChartCard
            title="Flächenbilanz je Warengruppe"
            help="Belegte gegen empfohlene Paletten je Warengruppe. Nur Warengruppen mit mindestens einem Artikel bekannter Palettengröße."
            loading={loading}
            error={error}
            empty={!loading && rows.length === 0}
            height={height}
        >
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={rows} layout="vertical" margin={{top: 4, right: 24, left: 8, bottom: 4}}>
                    <CartesianGrid horizontal={false} strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => formatNumber(v)} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="label" width={110} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(value, name) => [formatPallets(value), name]} />
                    <Legend
                        formatter={(value) => (value === "pallets_current" ? "Belegt heute" : "Empfohlen")}
                        wrapperStyle={{fontSize: 12}}
                    />
                    <Bar dataKey="pallets_current" name="pallets_current" fill={CURRENT_COLOR} radius={[0, 4, 4, 0]} barSize={14} />
                    <Bar dataKey="pallets_target" name="pallets_target" fill={TARGET_COLOR} radius={[0, 4, 4, 0]} barSize={14} />
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}
